import { create } from "zustand";
import type { ITicket, ITicketStats } from "./tickets-types";
import * as api from "./tickets-api";

interface TicketsState {
  list: ITicket[];
  total: number;
  stats: ITicketStats | null;
  page: number;
  limit: number;
  statusFilter: string;
  priorityFilter: string;
  query: string;
  showDeleted: boolean;
  loading: boolean;
  selectedTicket: ITicket | null;
  modalView: boolean;
  modalDelete: boolean;

  fetch: () => Promise<void>;
  fetchStats: () => Promise<void>;
  setPage: (p: number) => void;
  setLimit: (l: number) => void;
  setStatusFilter: (s: string) => void;
  setPriorityFilter: (p: string) => void;
  setQuery: (q: string) => void;
  setShowDeleted: (v: boolean) => void;
  openViewModal: (ticket: ITicket) => void;
  closeViewModal: () => void;
  openDeleteModal: (ticket: ITicket) => void;
  closeDeleteModal: () => void;
}

let debounceTimer: ReturnType<typeof setTimeout>;

export const useTicketsStore = create<TicketsState>((set, get) => ({
  list: [],
  total: 0,
  stats: null,
  page: 1,
  limit: 25,
  statusFilter: "",
  priorityFilter: "",
  query: "",
  showDeleted: false,
  loading: false,
  selectedTicket: null,
  modalView: false,
  modalDelete: false,

  fetch: async () => {
    const { query, statusFilter, priorityFilter, showDeleted, page, limit } =
      get();
    set({ loading: true });
    try {
      const res = await api.getTickets({
        q: query || undefined,
        status: statusFilter || undefined,
        priority: priorityFilter || undefined,
        show_deleted: showDeleted || undefined,
        page,
        limit,
      });
      set({ list: res.tickets ?? [], total: res.total, loading: false });
    } catch {
      set({ loading: false });
    }
  },

  fetchStats: async () => {
    try {
      const stats = await api.getTicketStats();
      set({ stats });
    } catch {
      // статистика не критична
    }
  },

  setPage: (page) => {
    set({ page });
    get().fetch();
  },

  setLimit: (limit) => {
    set({ limit, page: 1 });
    get().fetch();
  },

  setStatusFilter: (statusFilter) => {
    set({ statusFilter, page: 1 });
    get().fetch();
  },

  setPriorityFilter: (priorityFilter) => {
    set({ priorityFilter, page: 1 });
    get().fetch();
  },

  setQuery: (query) => {
    set({ query, page: 1 });
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => get().fetch(), 300);
  },

  setShowDeleted: (showDeleted) => {
    set({ showDeleted, page: 1 });
    get().fetch();
  },

  openViewModal: (ticket) => set({ selectedTicket: ticket, modalView: true }),
  closeViewModal: () => set({ modalView: false, selectedTicket: null }),
  openDeleteModal: (ticket) =>
    set({ selectedTicket: ticket, modalDelete: true }),
  closeDeleteModal: () => set({ modalDelete: false }),
}));
